import { formatCurrency } from '@if/core/utils'
import {
  ActionButton,
  Banner,
  BodyText,
  FieldLabel,
  FormBody,
  FormContainerProvider,
  StrongValue,
  useFormContainerContext,
  useSnackbar,
} from '@if/mui'
import { Checkbox, FormControlLabel, Skeleton, Stack } from '@mui/material'
import { useCreateBankJob, useDisburseLenderPayments } from '@src/api/lender-payment/disbursement-api'
import {
  useGetPendingLenderPayments,
  type PendingLenderPaymentDto,
} from '@src/api/lender-payment/pending-lender-payments-api'
import { useCompany } from '@src/services/company-hooks'
import { getToday } from '@src/services/DateTimeProvider'
import { EPaymentMethod } from '@src/types/constants'
import type { UseQueryResult } from '@tanstack/react-query'
import { useEffect, useMemo, useState } from 'react'
import { useWatch, type UseFormReturn } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { useAptPayDetailsDialog } from './AptPayDetailsDialog-hook'
import { useChequeDetailsDialog } from './ChequeDetailsDialog-hook'
import DisburseActions from './DisburseActions'
import {
  disbursementSelectionSchema,
  EDisbursementAction,
  isDisbursementAction,
  type DisbursementSelectionSchemaType,
} from './disbursement-selection-schema'
import PayeeTypeCard from './PayeeTypeCard'
import {
  groupByPayeeType,
  hasNegativeSelectedTotal,
  isBeyondFutureWindow,
  isFuturePayment,
  keepListed,
  payeeCountOf,
  sumAmounts,
  toggleAll,
  toggleOne,
} from './pending-lender-payment-grouping'

const ALL_PENDING_LIMIT = 1000
const SELECTION_FIELD = 'lenderPaymentOperationIds'
const NOTHING_SELECTED_BLOCKER = 'disburseNothingSelected'
const INVALID_PAYEE_BLOCKER = 'disburseNegativePayeeTotal'

const PendingLenderPaymentSelection = () => {
  const { t } = useTranslation()
  const { showError } = useSnackbar()
  const financingCompanyId = useCompany()
  const pendingQuery = useGetPendingLenderPayments({
    financingCompanyId,
    searchTerm: '',
    offset: 0,
    limit: ALL_PENDING_LIMIT,
  })
  const createBankJob = useCreateBankJob()
  const disburse = useDisburseLenderPayments()
  const chequeDetailsDialog = useChequeDetailsDialog()
  const aptPayConfirmDialog = useAptPayDetailsDialog()

  const applyCheque = async (lenderPaymentOperationIds: number[]): Promise<boolean> => {
    // A cheque pays one payee, so a selection spanning several is refused and left untouched.
    const selectedIds = new Set(lenderPaymentOperationIds)
    const selectedPayments = (pendingQuery.data ?? []).filter((p) => selectedIds.has(p.operationId))
    if (payeeCountOf(selectedPayments) > 1) {
      showError(t('disburse.cheque.singlePayeeOnly'))
      return false
    }

    // The cheque's number and date are asked for on the way out; cancelling the dialog
    // leaves the selection untouched, with no success message.
    const cheque = await chequeDetailsDialog.create({ paymentCount: lenderPaymentOperationIds.length })
    if (cheque === null) return false

    await createBankJob.mutateAsync({
      financingCompanyId,
      lenderPaymentOperationIds,
      paymentMethodId: EPaymentMethod.Cheque,
      paymentDate: cheque.chequeDate.toString(),
      checkNumber: cheque.chequeNumber,
    })

    return true
  }

  const applyAptPay = async (lenderPaymentOperationIds: number[]): Promise<boolean> => {
    const confirmed = await aptPayConfirmDialog.open({ paymentCount: lenderPaymentOperationIds.length })
    if (!confirmed) return false

    await createBankJob.mutateAsync({
      financingCompanyId,
      lenderPaymentOperationIds,
      paymentMethodId: EPaymentMethod.APTPay,
      paymentDate: getToday().toString(),
      checkNumber: null,
    })

    return true
  }

  const OnDisbursementSubmit = async (
    values: { lenderPaymentOperationIds: number[] },
    _methods: UseFormReturn<{ lenderPaymentOperationIds: number[] }, unknown, { lenderPaymentOperationIds: number[] }>,
    origin: string,
  ) => {
    if (!isDisbursementAction(origin)) throw new Error(`Unknown disbursement action: ${origin}`)
    const lenderPaymentOperationIds = values.lenderPaymentOperationIds

    if (origin === EDisbursementAction.ApplyCheque) {
      return await applyCheque(lenderPaymentOperationIds)
    } else if (origin === EDisbursementAction.AptPay) {
      return await applyAptPay(lenderPaymentOperationIds)
    }

    await disburse.mutateAsync({ action: origin, lenderPaymentOperationIds })
  }

  return (
    <FormContainerProvider<DisbursementSelectionSchemaType>
      mode="create"
      useFormSetup={() => ({ schema: disbursementSelectionSchema })}
      onSubmit={OnDisbursementSubmit}
      successMessage={(_result, values) =>
        t('disburse.selection.submitSuccess', { count: values.lenderPaymentOperationIds.length })
      }
    >
      <Stack spacing={2}>
        <DisburseActions />
        <FormBody<DisbursementSelectionSchemaType>>
          {(methods) => <SelectionBody methods={methods} pendingQuery={pendingQuery} />}
        </FormBody>
      </Stack>
    </FormContainerProvider>
  )
}

interface SelectionBodyProps {
  methods: Pick<UseFormReturn<DisbursementSelectionSchemaType>, 'control' | 'setValue'>
  pendingQuery: UseQueryResult<PendingLenderPaymentDto[]>
}

const SelectionBody = ({ methods, pendingQuery }: SelectionBodyProps) => {
  const { t } = useTranslation()
  const { data, isLoading, isError } = pendingQuery

  const today = useMemo(() => getToday(), [])
  const [showFuture, setShowFuture] = useState(false)

  const selectedIds = useWatch({ control: methods.control, name: SELECTION_FIELD })
  const selected = useMemo<ReadonlySet<number>>(() => new Set(selectedIds), [selectedIds])

  const { setBusy } = useFormContainerContext()
  useEffect(() => {
    setBusy(NOTHING_SELECTED_BLOCKER, selected.size === 0)
  }, [selected.size, setBusy])

  const setSelected = (next: ReadonlySet<number>) =>
    methods.setValue(SELECTION_FIELD, [...next], { shouldDirty: true, shouldValidate: true })

  // A successful submit re-seeds the form with the ids it sent, and those payments then leave the
  // list. Dropping them here keeps the actions gated on what the user can actually see.
  useEffect(() => {
    if (!data) return
    const listed = keepListed(selected, data)
    if (listed.size !== selected.size)
      methods.setValue(SELECTION_FIELD, [...listed], { shouldDirty: true, shouldValidate: true })
  }, [data, selected, methods])

  const visible = useMemo(() => {
    // The window caps the list first: even ticked, the screen never looks past it.
    const withinWindow = (data ?? []).filter((p) => !isBeyondFutureWindow(p, today))
    return showFuture ? withinWindow : withinWindow.filter((p) => !isFuturePayment(p, today))
  }, [data, showFuture, today])
  const groups = useMemo(() => groupByPayeeType(visible, today), [visible, today])

  const hasInvalidPayee = useMemo(
    () => groups.some((group) => group.payees.some((payee) => hasNegativeSelectedTotal(payee, selected))),
    [groups, selected],
  )
  useEffect(() => {
    setBusy(INVALID_PAYEE_BLOCKER, hasInvalidPayee)
  }, [hasInvalidPayee, setBusy])

  const selectedPayments = useMemo(() => (data ?? []).filter((p) => selected.has(p.operationId)), [data, selected])
  const selectedTotal = sumAmounts(selectedPayments)
  const selectedPayeeCount = payeeCountOf(selectedPayments)

  const handleToggleAll = (payments: PendingLenderPaymentDto[], select: boolean) =>
    setSelected(toggleAll(payments, selected, select))
  const handleToggleOne = (operationId: number) => setSelected(toggleOne(operationId, selected))

  return (
    <Stack spacing={2}>
      <FormControlLabel
        control={<Checkbox size="small" checked={showFuture} onChange={(e) => setShowFuture(e.target.checked)} />}
        label={t('disburse.selection.showFuture')}
      />

      {/* Always mounted, even with nothing selected: the first tick must not shift the cards down. */}
      <Banner
        severity="info"
        icon={false}
        action={
          <ActionButton size="small" disabled={selectedPayments.length === 0} onClick={() => setSelected(new Set())}>
            {t('disburse.selection.clear')}
          </ActionButton>
        }
      >
        <Stack direction="row" spacing={3} alignItems="baseline" flexWrap="wrap" useFlexGap>
          <StrongValue>{t('disburse.selection.selectedCount', { count: selectedPayments.length })}</StrongValue>
          <FieldLabel>{t('disburse.selection.payeeCount', { count: selectedPayeeCount })}</FieldLabel>
          <Stack direction="row" spacing={1} alignItems="baseline">
            <FieldLabel>{t('disburse.selection.selectedAmount')}</FieldLabel>
            <StrongValue>{formatCurrency(selectedTotal)}</StrongValue>
          </Stack>
        </Stack>
      </Banner>

      {isLoading && <Skeleton variant="rounded" height={160} />}
      {isError && <Banner severity="error">{t('disburse.selection.loadError')}</Banner>}
      {!isLoading && !isError && groups.length === 0 && <BodyText>{t('disburse.selection.empty')}</BodyText>}

      {/* The payee-level messages say which ones; this says there is something to look at. */}
      {hasInvalidPayee && <Banner severity="error">{t('disburse.selection.invalidSelection')}</Banner>}

      {groups.map((group) => (
        <PayeeTypeCard
          key={group.payeeType}
          group={group}
          selected={selected}
          today={today}
          onToggleAll={handleToggleAll}
          onToggleOne={handleToggleOne}
        />
      ))}
    </Stack>
  )
}

export default PendingLenderPaymentSelection
